import React, { useState } from 'react';
import {
  SimpleGrid,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  Image,
  Badge,
  Box,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  WORLD_CUP_EDITIONS,
  ALL_TIME_TOP_SCORERS,
  CHAMPIONS_TITLES,
} from '../../data/worldCupHistory';
import { SportsCard } from '../ui/SportsCard';
import { ResponsiveTableContainer } from '../ui/ResponsiveTableContainer';
import { useAppColors } from '../../hooks/useAppColors';

const FIFA_FLAG: Record<string, string> = {
  ARG: 'ar', BRA: 'br', GER: 'de', FRA: 'fr', ITA: 'it', ESP: 'es',
  ENG: 'gb-eng', URU: 'uy', MEX: 'mx', NED: 'nl', CRO: 'hr', COL: 'co',
};

const flagUrl = (code: string) =>
  `https://flagcdn.com/w40/${FIFA_FLAG[code] || 'un'}.png`;

export const MundialHistorySection: React.FC = () => {
  const [view, setView] = useState<'editions' | 'scorers' | 'champions'>('editions');
  const { subtle } = useAppColors();
  const tabBg = useColorModeValue('gray.100', 'gray.700');
  const tabActive = useColorModeValue('pitch.600', 'pitch.400');
  const tabInactiveColor = useColorModeValue('gray.700', 'gray.200');

  const tabs = [
    { id: 'editions' as const, label: 'Por torneo' },
    { id: 'scorers' as const, label: 'Goleadores históricos' },
    { id: 'champions' as const, label: 'Más campeonatos' },
  ];

  return (
    <SimpleGrid spacing={6}>
      <HStack spacing={2} flexWrap="wrap">
        {tabs.map((t) => (
          <Badge
            key={t.id}
            px={3}
            py={1}
            borderRadius="md"
            cursor="pointer"
            bg={view === t.id ? tabActive : tabBg}
            color={view === t.id ? 'white' : tabInactiveColor}
            onClick={() => setView(t.id)}
          >
            {t.label}
          </Badge>
        ))}
      </HStack>

      {view === 'editions' && (
        <SportsCard>
          <Heading size="md" mb={4}>
            Campeones y máximo goleador por Mundial
          </Heading>
          <Box overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Año</Th>
                  <Th>Sede</Th>
                  <Th>Campeón</Th>
                  <Th>Subcampeón</Th>
                  <Th>Máx. goleador</Th>
                </Tr>
              </Thead>
              <Tbody>
                {WORLD_CUP_EDITIONS.map((ed) => (
                  <Tr key={ed.year}>
                    <Td fontWeight="bold">{ed.year}</Td>
                    <Td>{ed.host}</Td>
                    <Td>
                      <HStack>
                        <Image src={flagUrl(ed.championCode)} alt="" boxSize="20px" />
                        <Text>{ed.champion}</Text>
                      </HStack>
                    </Td>
                    <Td>{ed.runnerUp}</Td>
                    <Td>
                      {ed.topScorer.name} ({ed.topScorer.goals}) — {ed.topScorer.country}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </SportsCard>
      )}

      {view === 'scorers' && (
        <SportsCard accent>
          <Heading size="md" mb={2}>
            Máximos goleadores en la historia del Mundial
          </Heading>
          <Text fontSize="sm" color={subtle} mb={4}>
            Acumulado en todas las Copas del Mundo (1930–2022)
          </Text>
          <ResponsiveTableContainer>
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>#</Th>
                <Th>Jugador</Th>
                <Th>Selección</Th>
                <Th isNumeric>Goles</Th>
                <Th>Mundiales</Th>
              </Tr>
            </Thead>
            <Tbody>
              {ALL_TIME_TOP_SCORERS.map((s, i) => (
                <Tr key={s.name}>
                  <Td>{i + 1}</Td>
                  <Td fontWeight="semibold">{s.name}</Td>
                  <Td>{s.country}</Td>
                  <Td isNumeric>
                    <Badge bg="gold.500" color="slate.900">
                      {s.goals}
                    </Badge>
                  </Td>
                  <Td fontSize="sm">{s.editions}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
          </ResponsiveTableContainer>
        </SportsCard>
      )}

      {view === 'champions' && (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          {CHAMPIONS_TITLES.map((c) => (
            <SportsCard key={c.country}>
              <HStack justify="space-between">
                <Text fontWeight="bold" fontSize="lg">
                  {c.country}
                </Text>
                <Badge bg="gold.500" color="slate.900" fontSize="md" px={3}>
                  {c.titles} {c.titles === 1 ? 'título' : 'títulos'}
                </Badge>
              </HStack>
            </SportsCard>
          ))}
        </SimpleGrid>
      )}
    </SimpleGrid>
  );
};
